import {Injectable} from '@angular/core';
import {StageService} from './stage.service';
import {SceneryService} from './scenery.service';

declare let THREE: any;

interface IRepeat {
  x: number;
  y: number;
}

interface IDictionary {
  [key: string]: string;
}

interface IMateriaReplace {
  material: any;
  pattern: string[];
}

@Injectable()
export class SetMaterialsService {

  loader = new THREE.TextureLoader();

  private currentFloorTexture: any;
  private nameToImageMap: IDictionary = {
    'wood-redWood': 'feeney_wood_deck_redwood.jpg',
    'wood-ipe': 'feeney_wood_deck_Ipe.jpg',
    'wood-grey': 'feeney_wood_deck_grey.jpg',
    'wood-cedar': 'feeney_wood_deck_cedar.jpg',
    'tile-sand': 'feeney_tile_deck_sand.jpg',
    'tile-grey': 'feeney_tile_deck_grey.jpg',
    'concrete': 'feeney_concrete_deck.jpg',
    'composite-redWood': 'Feeney_composite_deck_redwood.jpg',
    'composite-ipe': 'feeney_composite_deck_Ipe.jpg',
    'composite-grey': 'feeney_composite_deck_grey.jpg',
    'composite-cedar': 'feeney_composite_deck_cedar.jpg',
    // 2026 refresh — 9 types from Feeney deck materials board (placeholders until final assets)
    'wood-pressure-treated': 'feeney_wood_deck_pressure_treated.jpg',
    'wood-thermally-modified': 'feeney_wood_deck_thermally_modified.jpg',
    'composite-capped': 'feeney_composite_deck_capped.jpg',
    'composite-traditional': 'feeney_composite_deck_traditional.jpg',
    'pvc-grey': 'feeney_pvc_deck_grey.jpg',
    'aluminum-grey': 'feeney_aluminum_deck_grey.jpg',
    'paver-porcelain': 'feeney_paver_deck_porcelain.jpg',
    'stone-natural': 'feeney_stone_deck_natural.jpg',
    'deck-tiles': 'feeney_deck_tiles.jpg',
    'default': 'cedar_deck_05a.jpg'
  };

  constructor(
    private stage: StageService,
    private scenery: SceneryService
  ) {
    this.currentFloorTexture = 'wood-redWood';
  }

  async setMaterial(textureName?: string) {
    if (textureName) {
      this.currentFloorTexture = textureName;
      if (textureName === 'default') {
        this.currentFloorTexture = 'wood-redWood';
      }
    }
    await this.changeMaterialToFloors();
    await this.setMaterialToTierSteps();
    await this.setMaterialForStairs();
  }

  private loadTopTexture(): Promise<any> {
    const url = `../../assets/images/texture/floor/${this.nameToImageMap[this.currentFloorTexture]}`;
    return this.loadTexture(url);
  }

  private loadSideTexture(): Promise<any> {
    return this.loadTexture('../../assets/images/deck_fascia.png');
  }

  private loadTexture(url): Promise<any> {
    return new Promise(ok => {
      this.loader.load(url, texture => {
        texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
        ok(texture);
      });
    });
  }

  getCurrentTextureUrl() {
    return `../../assets/images/texture/floor/${this.nameToImageMap[this.currentFloorTexture]}`;
  }

  private async createMaterialConfig(topRepeat: IRepeat, sideRepeat: IRepeat): Promise<{ top: any, side: any }> {

    const [topTexture, sideTexture] = await Promise.all([this.loadTopTexture(), this.loadSideTexture()]);

    topTexture.repeat.set(topRepeat.x, topRepeat.y);
    topTexture.needsUpdate = true;

    sideTexture.repeat.set(sideRepeat.x, sideRepeat.y);
    sideTexture.needsUpdate = true;

    return {
      top: {
        map: topTexture,
        color: 0xa3a3a3,
        metalness: 0.4,
        envMapIntensity: 1.8,
        roughness: 0.7,
        envMap: this.scenery.cubeMap()
      },
      side: {
        map: sideTexture,
        color: 0xffffff,
        specular: 0xffffff,
        shininess: 5
      }
    };
  }

  private async changeMaterialToFloors() {
    const material = await this.createMaterialConfig({x: 0.0118, y: 0.0118}, {x: 0.002, y: 0.009});
    const materials = [material.top, material.side];
    const floors = this.stage.getCompoundFloor();
    floors.children.forEach(floor => {
      floor.material.forEach((m, i) => {
        m.setValues(materials[i]);
        m.needsUpdate = true;
      });
    });
  }

  getCurrentFloorTexture(): any {
    return this.currentFloorTexture;
  }

  private setMaterialByPattern(rootObject, rules: IMateriaReplace[]) {
    rootObject.traverse(ch => {
      rules.forEach(rule => {
        if (rule.pattern.indexOf(ch.name) !== -1) {
          ch.material.setValues(rule.material);
          ch.material.needsUpdate = true;
        }
      });
    });
  }

  async setMaterialForStairs() {
    const material = await this.createMaterialConfig({x: 1, y: 1}, {x: 1, y: 1});
    const stairs = this.stage.getStairs();
    stairs.forEach(stair => {
      this.setMaterialByPattern(stair, [
        {material: material.top, pattern: ['Steps']},
        {material: material.side, pattern: ['Fascia', 'Riser']}
      ]);
    });
  }

  async setMaterialToTierSteps() {
    const floors = this.stage.getCompoundFloor();
    const material = await this.createMaterialConfig({x: 1, y: 3.5}, {x: 1, y: 1});
    floors.children.forEach(floor => {
      const tierSteps = floor.getObjectByName('tierSteps');
      if (tierSteps) {
        this.setMaterialByPattern(tierSteps, [
          {material: material.top, pattern: ['Steps']},
          {material: material.side, pattern: ['Fascia', 'Riser']}
        ]);
      }
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/services/set-materials.service.ts